// Custom EvoHR SVG emoji-style icons for navigation dropdowns
// Each icon is a rich, gradient-filled SVG designed for dark backgrounds

import React from "react";

interface IconProps {
  className?: string;
  size?: number;
}

const S = ({ className, size = 24, children, viewBox = "0 0 64 64" }: IconProps & { children: React.ReactNode; viewBox?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} width={size} height={size} className={className} style={{ display: "inline-block", verticalAlign: "middle" }}>
    {children}
  </svg>
);

// ── Solutions Icons ──

export const EvoTeamIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="team-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
    </defs>
    <circle cx="22" cy="20" r="8" fill="url(#team-g)" />
    <circle cx="42" cy="20" r="8" fill="url(#team-g)" />
    <ellipse cx="22" cy="46" rx="14" ry="10" fill="url(#team-g)" opacity=".85" />
    <ellipse cx="42" cy="46" rx="14" ry="10" fill="url(#team-g)" opacity=".85" />
    <circle cx="32" cy="16" r="9" fill="#93c5fd" />
    <ellipse cx="32" cy="42" rx="15" ry="11" fill="#93c5fd" />
  </S>
);

export const EvoPayrollIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="pay-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <rect x="8" y="12" width="48" height="40" rx="6" fill="url(#pay-g)" />
    <rect x="14" y="18" width="36" height="28" rx="3" fill="#064e3b" opacity=".3" />
    <text x="32" y="40" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#ecfdf5" fontFamily="system-ui">$</text>
    <rect x="8" y="8" width="48" height="8" rx="4" fill="#6ee7b7" />
  </S>
);

export const EvoCRMIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="crm-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#f472b6" />
        <stop offset="100%" stopColor="#db2777" />
      </linearGradient>
    </defs>
    <rect x="6" y="14" width="52" height="38" rx="6" fill="url(#crm-g)" />
    <rect x="12" y="8" width="40" height="10" rx="5" fill="#fce7f3" />
    <rect x="14" y="24" width="16" height="3" rx="1.5" fill="#fce7f3" opacity=".9" />
    <rect x="14" y="30" width="24" height="3" rx="1.5" fill="#fce7f3" opacity=".6" />
    <rect x="14" y="36" width="20" height="3" rx="1.5" fill="#fce7f3" opacity=".4" />
    <circle cx="46" cy="30" r="8" fill="#fce7f3" opacity=".2" />
    <path d="M43 30l3 3 5-6" stroke="#fce7f3" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </S>
);

export const EvoOpsIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="ops-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#ops-g)" />
    <circle cx="32" cy="32" r="18" fill="#451a03" opacity=".2" />
    <circle cx="32" cy="32" r="4" fill="#fef3c7" />
    <rect x="30" y="12" width="4" height="12" rx="2" fill="#fef3c7" />
    <rect x="30" y="40" width="4" height="12" rx="2" fill="#fef3c7" opacity=".6" />
    <rect x="12" y="30" width="12" height="4" rx="2" fill="#fef3c7" opacity=".6" />
    <rect x="40" y="30" width="12" height="4" rx="2" fill="#fef3c7" />
  </S>
);

export const EvoCommIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="comm-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#818cf8" />
        <stop offset="100%" stopColor="#6366f1" />
      </linearGradient>
    </defs>
    <rect x="6" y="10" width="40" height="30" rx="8" fill="url(#comm-g)" />
    <polygon points="18,40 26,40 14,52" fill="url(#comm-g)" />
    <circle cx="18" cy="25" r="3" fill="#e0e7ff" />
    <circle cx="28" cy="25" r="3" fill="#e0e7ff" />
    <circle cx="38" cy="25" r="3" fill="#e0e7ff" opacity=".5" />
    <rect x="30" y="18" width="28" height="22" rx="6" fill="#a5b4fc" />
    <rect x="36" y="26" width="16" height="2.5" rx="1.2" fill="#312e81" opacity=".4" />
    <rect x="36" y="31" width="10" height="2.5" rx="1.2" fill="#312e81" opacity=".3" />
  </S>
);

export const EvoHotelIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="hot-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#b45309" />
      </linearGradient>
    </defs>
    <rect x="10" y="20" width="44" height="34" rx="4" fill="url(#hot-g)" />
    <rect x="6" y="16" width="52" height="8" rx="4" fill="#fde68a" />
    <rect x="24" y="34" width="16" height="20" rx="2" fill="#451a03" opacity=".4" />
    <circle cx="36" cy="44" r="1.5" fill="#fef3c7" />
    <rect x="14" y="26" width="8" height="8" rx="1" fill="#fef3c7" opacity=".6" />
    <rect x="42" y="26" width="8" height="8" rx="1" fill="#fef3c7" opacity=".6" />
    <polygon points="8,16 32,4 56,16" fill="#fde68a" />
  </S>
);

export const EvoRestaurantIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="rest-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fb923c" />
        <stop offset="100%" stopColor="#ea580c" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="40" rx="26" ry="6" fill="#7c2d12" opacity=".3" />
    <ellipse cx="32" cy="36" rx="24" ry="18" fill="url(#rest-g)" />
    <ellipse cx="32" cy="36" rx="20" ry="14" fill="#fed7aa" opacity=".3" />
    <ellipse cx="32" cy="30" rx="24" ry="4" fill="#fdba74" />
    <rect x="30" y="8" width="4" height="14" rx="2" fill="#d1d5db" />
    <ellipse cx="32" cy="8" rx="6" ry="3" fill="#d1d5db" opacity=".5" />
  </S>
);

export const EvoRecruitIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="rec-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#2dd4bf" />
        <stop offset="100%" stopColor="#0d9488" />
      </linearGradient>
    </defs>
    <circle cx="26" cy="22" r="10" fill="url(#rec-g)" />
    <ellipse cx="26" cy="46" rx="16" ry="10" fill="url(#rec-g)" opacity=".85" />
    <circle cx="46" cy="18" r="10" fill="none" stroke="#5eead4" strokeWidth="3" />
    <line x1="53" y1="25" x2="60" y2="32" stroke="#5eead4" strokeWidth="3" strokeLinecap="round" />
    <circle cx="46" cy="18" r="4" fill="#5eead4" opacity=".3" />
  </S>
);

// ── Industries Icons ──

export const EvoHealthIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="health-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f87171" />
        <stop offset="100%" stopColor="#dc2626" />
      </linearGradient>
    </defs>
    <rect x="8" y="8" width="48" height="48" rx="12" fill="url(#health-g)" />
    <rect x="26" y="16" width="12" height="32" rx="3" fill="#fef2f2" />
    <rect x="16" y="26" width="32" height="12" rx="3" fill="#fef2f2" />
  </S>
);

export const EvoEduIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="edu-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a78bfa" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
    </defs>
    <polygon points="32,6 60,22 32,38 4,22" fill="url(#edu-g)" />
    <polygon points="32,38 4,22 4,38 32,54" fill="#8b5cf6" opacity=".7" />
    <polygon points="32,38 60,22 60,38 32,54" fill="#6d28d9" opacity=".5" />
    <rect x="52" y="22" width="4" height="24" rx="2" fill="#c4b5fd" />
    <circle cx="54" cy="48" r="4" fill="#c4b5fd" />
  </S>
);

export const EvoRetailIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="ret-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f9a8d4" />
        <stop offset="100%" stopColor="#ec4899" />
      </linearGradient>
    </defs>
    <rect x="10" y="22" width="44" height="32" rx="4" fill="url(#ret-g)" />
    <path d="M10 22 L18 8 L46 8 L54 22" fill="#fce7f3" />
    <path d="M22 22 C22 16 18 8 18 8" stroke="#f472b6" strokeWidth="2" fill="none" />
    <path d="M42 22 C42 16 46 8 46 8" stroke="#f472b6" strokeWidth="2" fill="none" />
    <rect x="26" y="34" width="12" height="14" rx="2" fill="#831843" opacity=".3" />
  </S>
);

export const EvoConstructIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="con-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
    </defs>
    <path d="M8 32 L32 8 L56 32 Z" fill="url(#con-g)" />
    <rect x="12" y="32" width="40" height="24" rx="2" fill="#92400e" />
    <rect x="24" y="36" width="16" height="20" rx="2" fill="#451a03" opacity=".5" />
    <rect x="16" y="38" width="6" height="6" rx="1" fill="#fde68a" opacity=".7" />
    <rect x="42" y="38" width="6" height="6" rx="1" fill="#fde68a" opacity=".7" />
    <rect x="26" y="4" width="12" height="6" rx="3" fill="#fde68a" />
  </S>
);

export const EvoTechIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="tech-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#67e8f9" />
        <stop offset="100%" stopColor="#06b6d4" />
      </linearGradient>
    </defs>
    <rect x="8" y="10" width="48" height="32" rx="4" fill="#164e63" />
    <rect x="12" y="14" width="40" height="24" rx="2" fill="url(#tech-g)" />
    <text x="32" y="31" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#083344" fontFamily="monospace">&gt;_</text>
    <rect x="20" y="44" width="24" height="4" rx="2" fill="#94a3b8" />
    <rect x="14" y="50" width="36" height="3" rx="1.5" fill="#94a3b8" opacity=".5" />
  </S>
);

export const EvoManufactIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="mfg-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a1a1aa" />
        <stop offset="100%" stopColor="#71717a" />
      </linearGradient>
    </defs>
    <rect x="6" y="28" width="20" height="28" rx="2" fill="url(#mfg-g)" />
    <rect x="30" y="20" width="16" height="36" rx="2" fill="url(#mfg-g)" />
    <rect x="50" y="34" width="10" height="22" rx="2" fill="url(#mfg-g)" />
    <rect x="10" y="8" width="4" height="20" rx="2" fill="#d4d4d8" />
    <rect x="36" y="6" width="4" height="14" rx="2" fill="#d4d4d8" />
    <circle cx="12" cy="6" r="4" fill="#e4e4e7" opacity=".5" />
    <circle cx="38" cy="4" r="3" fill="#e4e4e7" opacity=".4" />
    <circle cx="16" cy="40" r="5" fill="#3f3f46" />
    <circle cx="38" cy="36" r="5" fill="#3f3f46" />
  </S>
);

export const EvoFinanceIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="fin-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1e3a5f" />
        <stop offset="100%" stopColor="#0f1b3d" />
      </linearGradient>
    </defs>
    <rect x="8" y="12" width="48" height="40" rx="6" fill="url(#fin-g)" />
    <polygon points="32,6 8,12 56,12" fill="#1e40af" />
    <rect x="16" y="24" width="8" height="20" rx="2" fill="#60a5fa" opacity=".8" />
    <rect x="28" y="18" width="8" height="26" rx="2" fill="#93c5fd" opacity=".8" />
    <rect x="40" y="28" width="8" height="16" rx="2" fill="#60a5fa" opacity=".6" />
    <rect x="14" y="48" width="36" height="2" rx="1" fill="#bfdbfe" opacity=".3" />
  </S>
);

export const EvoGovIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="gov-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#d4d4d8" />
        <stop offset="100%" stopColor="#a1a1aa" />
      </linearGradient>
    </defs>
    <polygon points="32,4 6,18 58,18" fill="url(#gov-g)" />
    <rect x="6" y="18" width="52" height="4" rx="1" fill="#e4e4e7" />
    <rect x="12" y="22" width="6" height="26" rx="1" fill="url(#gov-g)" />
    <rect x="22" y="22" width="6" height="26" rx="1" fill="url(#gov-g)" />
    <rect x="36" y="22" width="6" height="26" rx="1" fill="url(#gov-g)" />
    <rect x="46" y="22" width="6" height="26" rx="1" fill="url(#gov-g)" />
    <rect x="6" y="48" width="52" height="6" rx="2" fill="#e4e4e7" />
    <rect x="4" y="54" width="56" height="4" rx="1" fill="#d4d4d8" />
  </S>
);

// ── Resources Icons ──

export const EvoBlogIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="blog-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <rect x="8" y="6" width="48" height="52" rx="6" fill="url(#blog-g)" />
    <rect x="14" y="14" width="28" height="4" rx="2" fill="#451a03" opacity=".4" />
    <rect x="14" y="22" width="36" height="2.5" rx="1.2" fill="#fef3c7" opacity=".7" />
    <rect x="14" y="28" width="36" height="2.5" rx="1.2" fill="#fef3c7" opacity=".5" />
    <rect x="14" y="34" width="24" height="2.5" rx="1.2" fill="#fef3c7" opacity=".4" />
    <rect x="14" y="42" width="36" height="2.5" rx="1.2" fill="#fef3c7" opacity=".5" />
    <rect x="14" y="48" width="20" height="2.5" rx="1.2" fill="#fef3c7" opacity=".3" />
  </S>
);

export const EvoHelpIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="help-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#38bdf8" />
        <stop offset="100%" stopColor="#0284c7" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#help-g)" />
    <text x="32" y="40" textAnchor="middle" fontSize="30" fontWeight="bold" fill="#f0f9ff" fontFamily="system-ui">?</text>
  </S>
);

export const EvoAPIIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="api-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#4ade80" />
        <stop offset="100%" stopColor="#16a34a" />
      </linearGradient>
    </defs>
    <rect x="6" y="10" width="52" height="44" rx="8" fill="#14532d" />
    <text x="32" y="30" textAnchor="middle" fontSize="11" fontWeight="bold" fill="url(#api-g)" fontFamily="monospace">{"{ }"}</text>
    <rect x="14" y="38" width="20" height="2.5" rx="1.2" fill="#4ade80" opacity=".5" />
    <rect x="14" y="44" width="14" height="2.5" rx="1.2" fill="#4ade80" opacity=".3" />
  </S>
);

export const EvoCaseStudyIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="case-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
    </defs>
    <polygon points="32,6 38,22 56,22 42,32 46,50 32,40 18,50 22,32 8,22 26,22" fill="url(#case-g)" />
    <polygon points="32,14 36,24 48,24 38,30 42,42 32,34 22,42 26,30 16,24 28,24" fill="#fef3c7" opacity=".4" />
  </S>
);

export const EvoWebinarIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="web-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#c084fc" />
        <stop offset="100%" stopColor="#9333ea" />
      </linearGradient>
    </defs>
    <rect x="6" y="10" width="52" height="34" rx="6" fill="url(#web-g)" />
    <polygon points="28,20 28,38 44,29" fill="#f3e8ff" />
    <rect x="20" y="48" width="24" height="4" rx="2" fill="#d8b4fe" opacity=".5" />
    <rect x="28" y="52" width="8" height="6" rx="1" fill="#d8b4fe" opacity=".3" />
  </S>
);

export const EvoStatusIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="stat-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#34d399" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#stat-g)" />
    <path d="M20 32 L28 40 L44 24" stroke="#ecfdf5" strokeWidth="5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </S>
);

// ── Add-on / Pricing Icons ──

export const EvoUsersAddonIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="uadd-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#a78bfa" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#uadd-g)" />
    <circle cx="26" cy="26" r="7" fill="#ede9fe" />
    <ellipse cx="26" cy="42" rx="10" ry="6" fill="#ede9fe" />
    <circle cx="40" cy="24" r="6" fill="#ddd6fe" opacity="0.8" />
    <ellipse cx="40" cy="38" rx="8" ry="5" fill="#ddd6fe" opacity="0.8" />
  </S>
);

export const EvoStorageAddonIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="sadd-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#67e8f9" />
        <stop offset="100%" stopColor="#0891b2" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#sadd-g)" />
    <rect x="18" y="22" width="28" height="8" rx="2" fill="#ecfeff" />
    <rect x="18" y="34" width="28" height="8" rx="2" fill="#cffafe" />
    <circle cx="22" cy="26" r="2" fill="#0891b2" />
    <circle cx="22" cy="38" r="2" fill="#0891b2" />
    <rect x="34" y="25" width="8" height="2" rx="1" fill="#a5f3fc" />
    <rect x="34" y="37" width="8" height="2" rx="1" fill="#a5f3fc" />
  </S>
);

export const EvoAICreditsIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="aicr-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#aicr-g)" />
    <rect x="22" y="20" width="20" height="24" rx="4" fill="#fef3c7" />
    <path d="M28 28 L32 24 L36 28 M32 24 V38" stroke="#92400e" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="28" cy="36" r="1.5" fill="#92400e" />
    <circle cx="36" cy="36" r="1.5" fill="#92400e" />
  </S>
);

export const EvoPrioritySupportIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="psup-g" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fb923c" />
        <stop offset="100%" stopColor="#ea580c" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#psup-g)" />
    <path d="M32 16 L36 26 H44 L38 32 L40 42 L32 36 L24 42 L26 32 L20 26 H28 Z" fill="#fff7ed" />
  </S>
);

export const EvoBrandingIcon = (p: IconProps) => (
  <S {...p}>
    <defs>
      <linearGradient id="brnd-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#f472b6" />
        <stop offset="100%" stopColor="#db2777" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#brnd-g)" />
    <rect x="20" y="20" width="24" height="24" rx="5" fill="#fce7f3" />
    <circle cx="32" cy="29" r="5" fill="#db2777" />
    <rect x="26" y="36" width="12" height="3" rx="1.5" fill="#db2777" />
  </S>
);
